import { supabase } from '@/integrations/supabase/client';
import { fetchClient } from '@/integrations/supabase/fetchClient';
import { unifyAuthError } from '@/utils/security';
import { logFailedLogin, logSuccessfulLogin, logRateLimitExceeded } from '@/utils/securityMonitoring';
import { UserProfile, UserRole, UserPermission } from '@/types/auth';
import { loginRateLimiter, passwordResetRateLimiter } from '@/utils/security/rateLimiter';
import { runFullConnectionDiagnostic } from '@/utils/supabaseConnectionTest';
import { clearSupabaseSession } from '@/utils/supabaseSessionCleaner';
import { browserDiagnostic } from '@/utils/browserDiagnostic';
import { dbClientToClient } from '@/utils/transformers/clientTransformers';
import { authWithTimeout, signUpWithTimeout, authCircuitBreaker, diagnoseAuth } from '@/utils/authDiagnostics';
import { authLogger } from '@/utils/authLogger';

// Локальная типизация RPC функции для обхода read-only types.ts
type GetCurrentUserRoleResponse = 'admin' | 'manager';

// Local DB row shapes to safely access properties from worker client
type DbProfile = {
  id: string | number;
  user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  created_at: string;
  updated_at?: string | null;
};

type StaffRoleRow = { role: string | null };

type UserRoleWithPermissions = {
  role: string;
  permissions?: { permission_name: string }[];
};

// Minimal session shape used for profile name/email resolution
type MinimalSession = {
  user?: {
    email?: string | null;
    user_metadata?: Record<string, unknown>;
  } | null;
} | null;

// Map staff roles to UserRole types (финальная система ролей)
const mapStaffRoleToUserRole = (staffRole: string): UserRole => {
  switch (staffRole) {
    case 'admin':
      return 'admin';
    case 'manager':
    case 'accountant': // accountant теперь manager с расширенными правами
    case 'marketer':   // marketer теперь manager с расширенными правами
      return 'manager';
    default:
      return 'manager'; // fallback
  }
};

// Определение разрешений на основе роли из БД
const getPermissionsFromStaffRole = (staffRole: string): UserPermission[] => {
  switch (staffRole) {
    case 'accountant':
      return ['accounting', 'finance', 'reports'];
    case 'marketer':
      return ['marketing', 'reports'];
    case 'admin':
      return ['staff_management', 'accounting', 'marketing', 'finance', 'reports'];

    default:
      return []; // обычный manager без дополнительных разрешений
  }
};

export const authService = {
  async signIn(email: string, password: string) {
    // Comprehensive logging
    authLogger.logAuthAttempt(email, 'signin');
    
    // Check rate limit before attempting sign in
    const { allowed, resetTime } = await loginRateLimiter.checkLimit(email, 'login');
    
    if (!allowed) {
      const resetDate = resetTime ? new Date(resetTime) : new Date();
      logRateLimitExceeded(email, 'login', { resetTime: resetDate.toISOString() });
      authLogger.logAuthFailure(email, 'Rate limit exceeded', 'signin', { resetTime: resetDate.toISOString() });
      throw new Error(`Too many login attempts. Try again at ${resetDate.toLocaleTimeString()}`);
    }

    const startTime = Date.now();
    
    try {
      // ИСПРАВЛЕНИЕ: Восстановлен Circuit Breaker после исправления ключей
      const { data, error } = await authCircuitBreaker.execute(async () => {
        return await authWithTimeout(email, password);
      });
      
      // Log circuit breaker state
      authLogger.logCircuitBreakerState(authCircuitBreaker.getState());
      
      if (error) {
        authLogger.logAuthFailure(email, error.message, 'signin', { 
          duration: Date.now() - startTime,
          errorCode: error.status || 'unknown'
        });
        throw error;
      }

      // Reset rate limit on successful login
      await loginRateLimiter.resetLimit(email, 'login');
      
      // Логируем успешный вход
      if (data.user) {
        logSuccessfulLogin(data.user.id, email, { 
          loginMethod: 'email_password',
          timestamp: new Date().toISOString()
        });
        authLogger.logAuthSuccess(email, data.user.id, 'signin');
      }
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const duration = Date.now() - startTime;
      
      // Enhanced error logging
      authLogger.logAuthFailure(email, errorMessage, 'signin', {
        duration,
        circuitBreakerState: authCircuitBreaker.getState()
      });
      
      // Log timeout specifically
      if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
        authLogger.logTimeout(email, 'signin', duration);
      }
      
      // Security monitoring
      logFailedLogin(email, errorMessage, {
        loginMethod: 'email_password',
        timestamp: new Date().toISOString()
      });
      
      // ИСПРАВЛЕНИЕ: Специальная обработка timeout ошибок
      if (errorMessage.includes('timeout') || errorMessage.includes('не отвечает') || errorMessage.includes('Timeout')) {
        throw new Error('Сервис аутентификации временно недоступен. Попробуйте позже или обратитесь к администратору.');
      }
      
      // Circuit breaker errors
      if (errorMessage.includes('Circuit Breaker')) {
        throw new Error('Сервис аутентификации временно отключен для восстановления. Попробуйте через 30 секунд.');
      }
      
      // Унифицируем ошибку для защиты от enumeration attacks
      const unifiedError = unifyAuthError(error);
      throw new Error(unifiedError);
    }
  },

  async signUp(email: string, password: string, userData?: Record<string, unknown>) {
    // TEMPORARILY DISABLED: Check rate limit for registration
    // const { allowed, resetTime } = await loginRateLimiter.checkLimit(email, 'register');
    
    // if (!allowed) {
    //   const resetDate = resetTime ? new Date(resetTime) : new Date();
    //   throw new Error(`Too many registration attempts. Try again at ${resetDate.toLocaleTimeString()}`);
    // }

    try {
      // ИСПРАВЛЕНИЕ: Используем Circuit Breaker и enhanced timeout handling
      const { data, error } = await authCircuitBreaker.execute(async () => {
        return await signUpWithTimeout(email, password, userData);
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // ИСПРАВЛЕНИЕ: Специальная обработка timeout ошибок
      if (errorMessage.includes('timeout') || errorMessage.includes('не отвечает') || errorMessage.includes('Timeout')) {
        throw new Error('Сервис регистрации временно недоступен. Попробуйте позже или обратитесь к администратору.');
      }
      
      throw error;
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      throw error;
    }
  },

  async resetPassword(email: string) {
    // Check rate limit for password reset
    const { allowed, resetTime } = await passwordResetRateLimiter.checkLimit(email, 'password_reset');
    
    if (!allowed) {
      const resetDate = resetTime ? new Date(resetTime) : new Date();
      throw new Error(`Too many password reset attempts. Try again at ${resetDate.toLocaleTimeString()}`);
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw error;
    }
  },

  async loadUserProfile(userId: string, session?: MinimalSession): Promise<UserProfile | null> {
    console.log('[authService] loadUserProfile: Loading profile for user:', userId);
    
    try {
      // ИСПРАВЛЕНИЕ: Используем supabaseWorker для запросов к БД (как все остальные сервисы)
      const t0 = performance.now?.() ?? Date.now();
      console.log('[authService] loadUserProfile: starting profiles query via supabaseWorker...');

      // Диагностический watchdog: если зависнет > 5s — лог предупреждение, но не прерываем
      let warned = false;
      const watchdog = setTimeout(() => {
        warned = true;
        console.warn('[authService] loadUserProfile: profiles query pending > 5000ms (possible CORS/Network stall)');
      }, 5000);

      const { data: profileData, error } = (await fetchClient
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()) as unknown as { data: DbProfile | null; error: unknown };

      clearTimeout(watchdog);
      const t1 = performance.now?.() ?? Date.now();
      console.log('[authService] loadUserProfile: profiles query completed', { durationMs: Math.round(t1 - t0), warned });
      
      if (error) {
        console.error('[authService] Worker proxy error:', error);
      }
      
      if (profileData) {
        console.log('[authService] Profile loaded via Worker proxy:', profileData);
        
        // Маппим профиль в UserProfile формат согласно Supabase схеме
        // ВАЖНО: если в таблице profiles нет first_name/last_name — используем user_metadata из session
        const meta = (session?.user?.user_metadata ?? {}) as Record<string, unknown>;
        const metaFirst = typeof meta.first_name === 'string' ? meta.first_name : undefined;
        const metaLast = typeof meta.last_name === 'string' ? meta.last_name : undefined;
        const metaFull = typeof meta.fullName === 'string' ? meta.fullName : undefined;
        const nameFromMeta = (metaFull || `${metaFirst || ''} ${metaLast || ''}`.trim()).trim();

        const nameFromProfile = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim();
        const resolvedFullName = (nameFromProfile || nameFromMeta || '').trim() || 'Unknown User';

        // Определяем фактическую роль из staff, отдавая приоритет собственному аккаунту пользователя
        let resolvedRoleFromStaff: UserRole | null = null;
        try {
          // 1) Находим аккаунт(ы), владельцем которых является пользователь
          const { data: ownerAccounts } = (await fetchClient
            .from('accounts')
            .select('id')
            .eq('user_id', userId)
            .limit(1)) as unknown as { data: { id: string }[] | null };

          // 2) Если найден собственный аккаунт — определяем роль по staff в этом аккаунте
          if (ownerAccounts && ownerAccounts.length > 0) {
            const ownerAccountId = ownerAccounts[0].id;
            const { data: ownerStaffRow } = (await fetchClient
              .from('staff')
              .select('role')
              .eq('user_id', userId)
              .eq('account_id', ownerAccountId)
              .maybeSingle()) as unknown as { data: StaffRoleRow | null };

            if (ownerStaffRow && ownerStaffRow.role) {
              resolvedRoleFromStaff = mapStaffRoleToUserRole(ownerStaffRow.role);
            }
          }

          // 3) Фоллбек: берем любую staff роль пользователя (если почему-то нет записи для собственного аккаунта)
          if (!resolvedRoleFromStaff) {
            const { data: anyStaffRow } = (await fetchClient
              .from('staff')
              .select('role')
              .eq('user_id', userId)
              .limit(1)
              .maybeSingle()) as unknown as { data: StaffRoleRow | null };

            if (anyStaffRow && anyStaffRow.role) {
              resolvedRoleFromStaff = mapStaffRoleToUserRole(anyStaffRow.role);
            }
          }
        } catch (e) {
          console.warn('[authService] loadUserProfile: failed to resolve role from staff, fallback to profile role. Error:', e);
        }

        // Вычисляем разрешения на основе staff роли (если определили)
        const staffRoleString = resolvedRoleFromStaff ? (resolvedRoleFromStaff as string) : (profileData.role || 'manager');
        const resolvedPermissions = getPermissionsFromStaffRole(String(staffRoleString));

        const userProfile: UserProfile = {
          id: profileData.id.toString(), // Конвертируем number в string
          userId: profileData.user_id || userId,
          email: session?.user?.email || 'unknown@example.com', // Берем email из session
          fullName: resolvedFullName,
          avatar: null, // В таблице profiles нет avatar_url
          role: (resolvedRoleFromStaff || (profileData.role as UserRole) || 'admin'),
          permissions: resolvedPermissions as UserPermission[],
          createdAt: profileData.created_at,
          updatedAt: profileData.updated_at || undefined,
          // Onboarding status
          onboardingCompleted: (profileData as any).onboarding_completed,
          onboardingCompletedAt: (profileData as any).onboarding_completed_at,
          onboardingStep: (profileData as any).onboarding_step
        };
        
        console.log('[authService] Profile loaded successfully via supabaseWorker:', userProfile);
        return userProfile;
      }

      return null;
    } catch (error) {
      console.error('[authService] Profile loading failed:', error);
      return null;
    }
  },

  async getCurrentUserRole(userId: string): Promise<UserRole | null> {
    try {
      // ИСПРАВЛЕНИЕ: Используем fetchClient для запросов к БД
      const { data: staffData, error } = (await fetchClient
        .from('staff')
        .select('role')
        .eq('user_id', userId)
        .single()) as unknown as { data: StaffRoleRow | null; error: unknown };
      
      if (error) {
        console.error('[authService] Error getting user role:', error);
        return null;
      }
      
      const role = mapStaffRoleToUserRole(String(staffData?.role || 'admin'));
      return role;
    } catch (error) {
      console.error('[authService] Error in getCurrentUserRole:', error);
      return 'admin';
    }
  },

  async verifyRoleAssignment(userId: string): Promise<{ success: boolean; role: UserRole | null; isFirstUser?: boolean }> {
    try {
      // ИСПРАВЛЕНИЕ: Используем fetchClient для запросов к БД
      const { data: rolesData, error: rolesError } = (await fetchClient
        .from('user_roles')
        .select(`
          role,
          permissions (
            permission_name
          )
        `)
        .eq('user_id', userId)) as unknown as { data: UserRoleWithPermissions[]; error: unknown };
      
      if (rolesError) {
        console.log('[authService] No staff record found for user:', userId);
        return { success: false, role: null };
      }
      
      if (!rolesData || rolesData.length === 0) {
        return { success: false, role: null };
      }
      const role = mapStaffRoleToUserRole(String(rolesData[0].role));
      const isFirstUser = role === 'admin';
      
      return {
        success: true,
        role,
        isFirstUser
      };
    } catch (error) {
      console.error('[authService] Error in verifyRoleAssignment:', error);
      return { success: false, role: null };
    }
  }
};
